import { ErrorComponent, Redirect } from 'admin';
import { EvidenceTypeEnum } from 'api';
import { Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { CenteredLoader } from 'ui';

import {
  SignInEmail,
  SignInEmailVerify,
  SignInPhone,
  SignInPhoneVerify,
  SignUp,
  SignUpCompany,
  SignUpSuccess,
  SignUpVerify,
} from '~/auth';
import {
  BuyerMaterialSpecificationsAddPage,
  BuyerMaterialSpecificationsEditPage,
  BuyerMaterialSpecificationsListPage,
} from '~/buyer-material-specifications';
import { DealCostDetails, DealRevenueDetails } from '~/dealhub';
import { DocumentPage } from '~/document-generation';
import { FinancingHome } from '~/financing';
import { GroundControl } from '~/ground-control';
import { InspectionCreate, InspectionDetails, InspectionsList } from '~/inspection';
import { InspectionCompanyList, InspectorList } from '~/inspection-company';
import { LoadsList } from '~/load';
import { ShipmentCreatePage, ShipmentUpdate } from '~/logistics';
import { TradingDashboard } from '~/matching';
import { MaterialProductCreate, MaterialProductDetails, MaterialProductsList } from '~/material-products';
import { Layout } from '~/navigation';
import { PurchaseOrderCreate, PurchaseOrderUpdate, SalesOrderCreate, SalesOrderUpdate } from '~/orders';
import { BuyPriceCalculator, SellPriceCalculator } from '~/price-calculator';
import { DealProfitabilityCalculator } from '~/profitability-calculator';
import {
  BalePhotos,
  Evidence,
  EvidenceEdit,
  ExternalEvidence,
  QualityHome,
  Sample,
  SampleEdit,
  Samples,
  SamplesEdit,
} from '~/quality';
import { SiteEvidence } from '~/site';
import {
  CompanySiteMaterials,
  InternalQualityReport,
  NewSiteMaterial,
  SiteMaterialAnalysisProvider,
  SiteMaterialUpdate,
} from '~/site-materials';
import { SupportTicketCreate } from '~/support';
import { FindTickets } from '~/tickets';
import { DemandSignalCreate, SupplySignalCreate } from '~/trade-signals';
import { TransportCostsEstimator } from '~/transport-costs';

import { GenerateDocumentPage } from './document-generation/pages/generate/Generate';
export const Router = () => {
  return (
    <Routes>
      <Route
        path="/ground-control"
        element={
          <Layout>
            <Outlet />
          </Layout>
        }
      >
        <Route index element={<GroundControl />} />
        <Route path="document-generation">
          <Route index element={<GenerateDocumentPage />} />
          <Route path=":id" element={<DocumentPage />} />
        </Route>
        <Route path="buy-price-calculator" element={<BuyPriceCalculator />} />
        <Route path="sell-price-calculator" element={<SellPriceCalculator />} />
        <Route path="profitability-calculator" element={<DealProfitabilityCalculator />} />
        <Route path="financing-home" element={<FinancingHome />} />
        <Route path="transport-costs">
          <Route path="estimate" element={<TransportCostsEstimator />} />
        </Route>
        <Route path="trade-signals">
          <Route path="demand">
            <Route path="create" element={<DemandSignalCreate />} />
          </Route>
          <Route path="supply">
            <Route path="create" element={<SupplySignalCreate />} />
          </Route>
        </Route>
        <Route path="material-products">
          <Route index element={<MaterialProductsList />} />
          <Route path="create" element={<MaterialProductCreate />} />
          <Route path=":id" element={<MaterialProductDetails />} />
        </Route>
        <Route path="create-purchase-order" element={<PurchaseOrderCreate />} />
        <Route path="create_purchase_order" element={<Navigate to="/ground-control/create-purchase-order/?" />} />
        <Route path="purchase-order/:id/update" element={<PurchaseOrderUpdate />} />
        <Route path="v2">
          <Route path="create-sales-order" element={<SalesOrderCreate />} />
          <Route path="sales-order/:id/update" element={<SalesOrderUpdate />} />
          <Route path="shipment">
            <Route path="create">
              <Route path=":salesOrderSlug" element={<ShipmentCreatePage />} />
            </Route>
            <Route path=":shipmentName" element={<ShipmentUpdate />} />
          </Route>
          <Route path="manage-company">
            <Route path=":companyId">
              <Route path="site-materials">
                <Route index element={<CompanySiteMaterials />} />
                <Route path=":siteMaterialId">
                  <Route index element={<Redirect to="/quality/:siteMaterialId" />} />
                  <Route path="report">
                    <Route index element={<InternalQualityReport />} />
                    <Route path="media" element={<Redirect to="/report/:siteMaterialId/media" />} />
                  </Route>
                  <Route path="update">
                    <Route index element={<SiteMaterialUpdate />} />
                  </Route>
                </Route>
                <Route path="new" element={<NewSiteMaterial />} />
              </Route>
              <Route
                path="material-specifications"
                element={
                  <Suspense fallback={<CenteredLoader />}>
                    <Outlet />
                  </Suspense>
                }
              >
                <Route index element={<BuyerMaterialSpecificationsListPage />} />
                <Route path=":buyerMaterialSpecificationId" element={<BuyerMaterialSpecificationsEditPage />} />
                <Route path="new" element={<BuyerMaterialSpecificationsAddPage />} />
              </Route>
            </Route>
            <Route path="*" element={<ErrorComponent />} />
          </Route>
        </Route>
        <Route path="trading" element={<TradingDashboard />} />
        <Route path="inspections">
          <Route index element={<InspectionsList />} />
          <Route path=":id" element={<InspectionDetails />} />
          <Route path="create" element={<InspectionCreate />} />
          <Route path="loads" element={<LoadsList />} />
          <Route path="companies" element={<InspectionCompanyList />} />
          <Route path="inspectors" element={<InspectorList />} />
        </Route>
      </Route>

      <Route path="accounts">
        <Route path="auth">
          <Route path="sign-in">
            <Route index element={<SignInPhone />} />
            <Route path="email">
              <Route index element={<SignInEmail />} />
              <Route path="verify" element={<SignInEmailVerify />} />
            </Route>
            <Route path="phone">
              <Route index element={<SignInPhone />} />
              <Route path="verify" element={<SignInPhoneVerify />} />
            </Route>
          </Route>
          <Route path="sign-up">
            <Route index element={<SignUp />} />
            <Route path="verify" element={<SignUpVerify />} />
            <Route path="company" element={<SignUpCompany />} />
            <Route path="success" element={<SignUpSuccess />} />
          </Route>

          <Route path="*" element={<ErrorComponent />} />
        </Route>
      </Route>

      <Route
        path="/post-deal/"
        element={
          <Layout>
            <Outlet />
          </Layout>
        }
      >
        <Route path="dealhub/deal/details/:deal_id/revenue/" element={<DealRevenueDetails />} />

        <Route path="dealhub/deal/details/:deal_id/cost/" element={<DealCostDetails />} />
      </Route>

      <Route
        path="support/ticket/"
        element={
          <Layout>
            <SupportTicketCreate />
          </Layout>
        }
      />
      <Route
        path="tickets/find/"
        element={
          <Layout>
            <FindTickets />
          </Layout>
        }
      />

      <Route
        path="quality"
        element={
          <Layout>
            <Suspense fallback={<CenteredLoader />}>
              <Outlet />
            </Suspense>
          </Layout>
        }
      >
        <Route index element={<SiteEvidence />} />
        <Route path=":siteMaterialId">
          <Route index element={<QualityHome />} />
          <Route path="ai-bale" element={<SiteMaterialAnalysisProvider />}>
            <Route index element={<BalePhotos />} />
            <Route path="edit" element={<EvidenceEdit evidenceType={EvidenceTypeEnum.BP} />} />
          </Route>
          <Route path="marketing" element={<Evidence evidenceType={EvidenceTypeEnum.MP} />} />
          <Route path="marketing/edit" element={<EvidenceEdit evidenceType={EvidenceTypeEnum.MP} />} />
          <Route path="loading" element={<Evidence evidenceType={EvidenceTypeEnum.LP} />} />
          <Route path="loading/edit" element={<EvidenceEdit evidenceType={EvidenceTypeEnum.LP} />} />
          <Route path="site" element={<Evidence evidenceType={EvidenceTypeEnum.SP} />} />
          <Route path="site/edit" element={<EvidenceEdit evidenceType={EvidenceTypeEnum.SP} />} />
          <Route path="sample">
            <Route index element={<Samples />} />
            <Route path="edit" element={<SamplesEdit />} />
            <Route path=":sampleId" element={<Sample />} />
            <Route path=":sampleId/edit" element={<SampleEdit />} />
          </Route>
        </Route>
        <Route path="external-evidence" element={<ExternalEvidence />} />
        <Route path="*" element={<ErrorComponent />} />
      </Route>
    </Routes>
  );
};
